import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { squarify, pnlColor, pnlLegend } from './treemap';

// Portfolio heatmap: area is weight, colour is change.
//
// Two dimensions at once, which a table cannot do — you see that the largest
// tile is red before reading a single ticker. One flat map of holdings, ranked
// by size; there is no sector nesting, because the question this answers is
// "what do I hold and how is it doing", and the breakdown below already covers
// composition.
//
// Everything scales with the tile it sits in. A tile too small for a logo drops
// it, too small for a percentage drops that, and too small for a ticker shows
// nothing at all — but every tile keeps its full figures in a tooltip, so the
// small ones stay readable rather than becoming decoration.

/** Tile size thresholds, in px, for progressively showing more. */
const SHOW_TICKER = { w: 34, h: 22 };
const SHOW_PCT = { w: 46, h: 40 };
const SHOW_LOGO = { w: 74, h: 76 };

const compact = (n) => {
    const abs = Math.abs(n);
    if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `${Math.round(n / 1_000)}k`;
    return String(Math.round(n));
};

const formatPct = (p) =>
    p === null || p === undefined || !Number.isFinite(p)
        ? null
        : `${p >= 0 ? '+' : '−'}${Math.abs(p).toFixed(2)}%`;

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

/**
 * Logos come from FMP's image CDN, which we already pay for.
 *
 * The symbol needs the same separator fix as the sector lookup: IBKR writes
 * Berkshire class B as `BRK B`, and the CDN only answers to `BRK-B`.
 */
const logoUrl = (symbol) =>
    `https://images.financialmodelingprep.com/symbol/${encodeURIComponent(
        symbol.replace(/[ .]/g, '-'),
    )}.png`;

/**
 * Hides itself if the symbol has no logo, rather than showing a broken image.
 *
 * Deliberately NOT loading="lazy". Only the larger tiles get a logo, so this is
 * a few dozen ~2KB images that are all on screen the moment the heatmap opens —
 * lazy loading defers nothing worth deferring, and the map is already behind a
 * toggle. It also does not load at all in a background tab, which makes the
 * whole thing appear broken under automation.
 */
function Logo({ symbol, size }) {
    const [failed, setFailed] = useState(false);
    if (failed) return null;
    return (
        <img
            className="heatmap__logo"
            src={logoUrl(symbol)}
            alt=""
            width={size}
            height={size}
            style={{ width: size, height: size }}
            onError={() => setFailed(true)}
        />
    );
}

function Tile({ tile, x, y, width: w, height: h, onSelect }) {
    const colour = pnlColor(tile.changePct);
    const showTicker = w >= SHOW_TICKER.w && h >= SHOW_TICKER.h;
    const showPct = w >= SHOW_PCT.w && h >= SHOW_PCT.h;
    const showLogo = tile.logoSymbol && w >= SHOW_LOGO.w && h >= SHOW_LOGO.h;

    // Type scales with the tile so a large holding reads as large. Bounded on
    // both sides: unbounded, one dominant position renders at 90px.
    const base = Math.min(w, h);
    const tickerSize = clamp(Math.min(base * 0.26, w / (tile.label.length * 0.62)), 9, 30);
    const pctSize = clamp(tickerSize * 0.68, 8, 17);
    const logoSize = clamp(base * 0.3, 16, 40);

    const Tag = onSelect ? 'button' : 'div';
    const pct = formatPct(tile.changePct);

    return (
        <Tag
            type={onSelect ? 'button' : undefined}
            className="heatmap__tile"
            onClick={onSelect ? () => onSelect(tile) : undefined}
            style={{
                left: x,
                top: y,
                width: w,
                height: h,
                background: colour.background,
                color: colour.text,
            }}
            title={`${tile.label}${tile.name ? ` — ${tile.name}` : ''} · ${compact(tile.value)}${
                pct ? ` · ${pct}` : ''
            }`}
        >
            {showLogo && <Logo symbol={tile.logoSymbol} size={logoSize} />}
            {showTicker && (
                <span className="heatmap__label" style={{ fontSize: tickerSize }}>
                    {tile.label}
                </span>
            )}
            {showPct && (
                <span className="heatmap__value" style={{ fontSize: pctSize }}>
                    {pct ?? compact(tile.value)}
                </span>
            )}
        </Tag>
    );
}

/** Measures the container so the layout can work in real pixels. */
function useWidth() {
    const ref = useRef(null);
    const [width, setWidth] = useState(0);

    useLayoutEffect(() => {
        const node = ref.current;
        if (!node) return undefined;
        const measure = () => setWidth(node.clientWidth);
        measure();
        // ResizeObserver, not a window listener: this element's width changes
        // when the sidebar opens without the window resizing at all.
        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', measure);
            return () => window.removeEventListener('resize', measure);
        }
        const observer = new ResizeObserver(measure);
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    return [ref, width];
}

export function HeatmapLegend({ label }) {
    return (
        <div className="hmlegend">
            <span className="hmlegend__label">{label}</span>
            <div className="hmlegend__scale">
                {pnlLegend().map((stop) => (
                    <span
                        key={stop.label}
                        className="hmlegend__swatch"
                        style={{ background: stop.background }}
                    >
                        {stop.label}
                    </span>
                ))}
            </div>
        </div>
    );
}

/**
 * @param {Array} tiles  {key, label, value, changePct, name?, logoSymbol?}
 */
export default function Heatmap({ tiles, height = 460, emptyMessage, onSelect }) {
    const [ref, width] = useWidth();

    const usable = useMemo(() => (tiles ?? []).filter((t) => Number(t.value) > 0), [tiles]);

    const layout = useMemo(
        () =>
            width <= 0 || usable.length === 0
                ? []
                : squarify(usable.map((t) => ({ value: t.value, tile: t })), width, height),
        [usable, width, height],
    );

    return (
        <div className="heatmap" ref={ref} style={{ height }}>
            {usable.length === 0 && (
                <p className="heatmap__empty">{emptyMessage ?? 'Nothing to show.'}</p>
            )}

            {layout.map((r) => (
                <Tile
                    key={r.item.tile.key}
                    tile={r.item.tile}
                    x={r.x}
                    y={r.y}
                    width={r.width}
                    height={r.height}
                    onSelect={onSelect}
                />
            ))}
        </div>
    );
}
